import {
  useMutation,
  UseMutationResult,
  useQueryClient,
} from '@tanstack/react-query';

import { updateCopiedTicketCheckIn } from '../queries/shadowCheckInupdate';
import {
  createNewCheckIn,
  submitCheckIn,
  deleteTicketCheckIn,
} from '../queries/submitCheckIns';
import { CheckInData } from '../types/types';
import { createPayload } from '../utils';

export function useCopyShadowCheckIns(): UseMutationResult<
  void,
  Error,
  {
    dataArray: CheckInData[];
    userId: string;
    userCheckInId: string;
    shadowId: string;
    dailySequence: number;
  }
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      dataArray,
      userId,
      userCheckInId,
      dailySequence,
    }: {
      dataArray: CheckInData[];
      userId: string;
      userCheckInId: string;
      shadowId: string;
      dailySequence: number;
    }) => {
      const checkInId =
        userCheckInId || (await createNewCheckIn(userId, dailySequence));
      const payloads = dataArray.map((data: CheckInData) =>
        createPayload(data, userId, checkInId, dailySequence, [], data.title),
      );
      await Promise.all(
        payloads.map(async (data) => {
          // Remove the id field before passing to submitCheckIn
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { id, ...dataWithoutId } = data;
          const insertResult = await submitCheckIn({ ...dataWithoutId });
          if (!insertResult) {
            throw new Error('Insert failed');
          }
          try {
            const updateResult = await updateCopiedTicketCheckIn(data.id || '');
            if (!updateResult) {
              throw new Error('Update failed');
            }
          } catch (error) {
            await deleteTicketCheckIn(insertResult, checkInId);
            throw error;
          }
        }),
      );
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['ticketCheckIns'] }),
        queryClient.invalidateQueries({ queryKey: ['checkInSequence'] }),
      ]);
    },
    onError: (error) => {
      throw error; // Re-throw the error so it can be handled by the component
    },
  });
}
